import { useState, useEffect, useCallback } from 'react';
import api from '../../../../../services/api';
import { fetchSectionVersions, fetchGlobalVersions, fetchVersionDetail, updateVersionLabel, revertToVersion, compareVersions, approveSectionVersion } from '../../../services/versionService';

// How often (ms) to quietly refresh the list while the version history panel is open.
const VERSION_POLL_INTERVAL_MS = 20000;

/**
 * Hook to manage version history state and operations
 */
export const useVersionHistory = (rfpId, activeSection, setEditableData, editableData) => {
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [versionsList, setVersionsList] = useState([]);
    const [activeVersionMenuId, setActiveVersionMenuId] = useState(null);
    const [isFetchingVersions, setIsFetchingVersions] = useState(false);
    const [isFetchingVersionDetails, setIsFetchingVersionDetails] = useState(false);
    const [isFetchingComparison, setIsFetchingComparison] = useState(false);

    // Additional modals state often associated with versioning
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [labelTargetVersion, setLabelTargetVersion] = useState(null);
    const [versionLabelText, setVersionLabelText] = useState('');

    const [showRevertModal, setShowRevertModal] = useState(false);
    const [revertTargetVersion, setRevertTargetVersion] = useState(null);
    const [revertReason, setRevertReason] = useState('Restoring to previous draft');

    const [showVersionDetailsModal, setShowVersionDetailsModal] = useState(false);
    const [selectedVersionData, setSelectedVersionData] = useState(null);
    const [isSelectingCompareFromView, setIsSelectingCompareFromView] = useState(false);
    const [compareSourceVersion, setCompareSourceVersion] = useState(null);

    // Helper to find section ID accurately across different model representations
    const getSectionId = useCallback((target) => {
        if (!editableData?.sections || !target) return null;
        const sectionObj = editableData.sections.find(s =>
            s.label === target ||
            s.key === target ||
            s.section_title === target ||
            s.section_key === target ||
            String(s.id) === String(target) ||
            String(s.section_id) === String(target)
        );
        return sectionObj?.section_id || sectionObj?.id || sectionObj?._id || null;
    }, [editableData]);

    // `silent` refreshes (background polling) skip the loading flag to avoid UI flicker.
    const fetchVersions = useCallback(async (silent = false) => {
        if (!rfpId || !activeSection) return;
        // Guard: callers pass fetchVersions as an event handler / callback in places
        const isSilent = silent === true;
        if (!isSilent) setIsFetchingVersions(true);
        try {
            const sectionId = getSectionId(activeSection);
            if (sectionId) {
                const response = await fetchSectionVersions(rfpId, sectionId);
                setVersionsList(response.data.versions || []);
            } else {
                // Fallback to legacy label search if ID not found
                const response = await fetchGlobalVersions(rfpId, { section: activeSection });
                setVersionsList(response.data.versions || []);
            }
        } catch (error) {
            console.error('Error fetching versions:', error);
        } finally {
            if (!isSilent) setIsFetchingVersions(false);
        }
    }, [rfpId, activeSection, getSectionId]);

    useEffect(() => {
        if (rfpId && activeSection && (editableData?.sections || showVersionHistory)) {
            fetchVersions();
        }
    }, [rfpId, activeSection, editableData, showVersionHistory, fetchVersions]);

    // The backend writes collaborative versions on its own schedule (periodic auto-save,
    // when the last editor leaves). Poll quietly while the panel is open so those new
    // versions appear without the user having to close and reopen it.
    useEffect(() => {
        if (!showVersionHistory || !rfpId || !activeSection) return;
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fetchVersions(true);
        }, VERSION_POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [showVersionHistory, rfpId, activeSection, fetchVersions]);

    const handleViewVersion = useCallback(async (v) => {
        setIsFetchingVersionDetails(true);
        try {
            // Use version_number for the URL as per user request (e.g., .../versions/2)
            const verNum = v.version_number || v.version_id || v.id;
            const sectionId = getSectionId(activeSection);

            if (sectionId && verNum) {
                const response = await fetchVersionDetail(rfpId, sectionId, verNum);
                setSelectedVersionData(response.data);
                setShowVersionDetailsModal(true);
            } else {
                console.error('Missing sectionId or versionNumber:', { sectionId, verNum });
            }
        } catch (error) {
            console.error('Error viewing version:', error);
        } finally {
            setIsFetchingVersionDetails(false);
        }
    }, [rfpId, activeSection, getSectionId]);

    const handleRenameVersion = useCallback(async (verNum, newLabel) => {
        if (!verNum || !newLabel) return;
        try {
            const sectionId = getSectionId(activeSection);
            if (!sectionId) throw new Error("Section ID not found");

            await updateVersionLabel(rfpId, sectionId, verNum, newLabel);
            fetchVersions();
        } catch (error) {
            console.error('Error renaming version:', error);
        }
    }, [rfpId, activeSection, getSectionId, fetchVersions]);

    const handleRevertVersion = useCallback(async () => {
        if (!revertTargetVersion) return;
        try {
            const verNum = revertTargetVersion.version_number || revertTargetVersion.version_id || revertTargetVersion.id;
            const sectionId = getSectionId(activeSection);
            if (!sectionId) throw new Error("Section ID not found");

            await revertToVersion(rfpId, sectionId, verNum, revertReason);
            
            setShowRevertModal(false);
            // Refresh main data after revert
            window.location.reload();
        } catch (error) {
            console.error('Error reverting version:', error);
        }
    }, [rfpId, activeSection, getSectionId, revertTargetVersion, revertReason]);

    const [comparisonData, setComparisonData] = useState(null);
    const [showComparisonModal, setShowComparisonModal] = useState(false);

    const fetchComparison = useCallback(async (v1Num, v2Num) => {
        if (!rfpId || !activeSection || !v1Num || !v2Num) return;
        setIsFetchingComparison(true);
        try {
            const sectionId = getSectionId(activeSection);
            if (sectionId) {
                const response = await compareVersions(rfpId, sectionId, v1Num, v2Num);
                setComparisonData(response.data);
                setShowComparisonModal(true);
            }
        } catch (error) {
            console.error('Error fetching comparison:', error);
        } finally {
            setIsFetchingComparison(false);
        }
    }, [rfpId, activeSection, getSectionId]);

    const [isApprovingVersion, setIsApprovingVersion] = useState(false);
    const [approvingVersionNum, setApprovingVersionNum] = useState(null);

    const handleApproveVersion = useCallback(async (versionNumber, onSuccess) => {
        if (!rfpId || !activeSection || !versionNumber) return;
        setIsApprovingVersion(true);
        setApprovingVersionNum(versionNumber);
        try {
            const sectionId = getSectionId(activeSection);
            if (!sectionId) {
                alert('Could not identify section ID to approve version.');
                return;
            }

            const response = await approveSectionVersion(rfpId, sectionId, versionNumber);
            const statusCode = response.data?.new_status_code;

            if (statusCode === 54 || response.status === 200 || response.status === 201) {
                if (typeof onSuccess === 'function') {
                    await onSuccess(versionNumber, response.data);
                }
                await fetchVersions();
                return { success: true, data: response.data };
            }
        } catch (error) {
            console.error('Approve version failed:', error);
            const httpStatus = error.response?.status;
            let msg;
            if (httpStatus === 409) {
                msg = error.response?.data?.detail || 'Cannot approve: Section must be Submitted (status 53), and version content cannot be empty.';
            } else if (httpStatus === 403) {
                msg = error.response?.data?.detail || 'Unauthorized: Only the assigned Team Lead can approve sections for this RFP.';
            } else if (httpStatus === 404) {
                msg = error.response?.data?.detail || `Version ${versionNumber} not found.`;
            } else {
                const detail = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to approve version.';
                msg = typeof detail === 'object' ? JSON.stringify(detail) : detail;
            }
            alert(msg);
            return { success: false, error: msg };
        } finally {
            setIsApprovingVersion(false);
            setApprovingVersionNum(null);
        }
    }, [rfpId, activeSection, getSectionId, fetchVersions]);

    return {
        showVersionHistory,
        setShowVersionHistory,
        versionsList,
        activeVersionMenuId,
        setActiveVersionMenuId,
        isFetchingVersions,
        isFetchingVersionDetails,
        fetchVersions,
        handleViewVersion,
        isApprovingVersion,
        approvingVersionNum,
        handleApproveVersion,

        // Comparison
        fetchComparison,
        comparisonData,
        showComparisonModal,
        setShowComparisonModal,

        // Modal states
        showLabelModal,
        setShowLabelModal,
        labelTargetVersion,
        setLabelTargetVersion,
        versionLabelText,
        setVersionLabelText,
        handleRenameVersion,

        showRevertModal,
        setShowRevertModal,
        revertTargetVersion,
        setRevertTargetVersion,
        revertReason,
        setRevertReason,
        handleRevertVersion,

        showVersionDetailsModal,
        setShowVersionDetailsModal,
        selectedVersionData,
        setSelectedVersionData,
        isSelectingCompareFromView,
        setIsSelectingCompareFromView,
        compareSourceVersion,
        setCompareSourceVersion,
        isFetchingComparison,
        setIsFetchingComparison
    };
};
